import { Link, NavLink, useNavigate } from "react-router-dom";
import { ShoppingBag, Menu, X, Heart, Search } from "lucide-react";
import { useState } from "react";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import { useI18n } from "@/context/I18nContext";

export default function Header() {
    const { setIsOpen, count } = useCart();
    const { count: wishCount } = useWishlist();
    const { t, lang, toggleLang } = useI18n();
    const navigate = useNavigate();
    const [mobileOpen, setMobileOpen] = useState(false);
    const [searchOpen, setSearchOpen] = useState(false);
    const [query, setQuery] = useState("");

    const navLink = ({ isActive }) =>
        `text-xs uppercase tracking-[0.2em] font-semibold hover:text-white transition-colors ${
            isActive ? "text-white" : "text-white/60"
        }`;

    const submitSearch = (e) => {
        e.preventDefault();
        const q = query.trim();
        if (q) navigate(`/collection?q=${encodeURIComponent(q)}`);
        setSearchOpen(false);
        setQuery("");
    };

    return (
        <header
            data-testid="site-header"
            className="fixed top-0 left-0 right-0 z-40 border-b border-white/10 bg-black/70 backdrop-blur-xl"
        >
            <div className="mx-auto max-w-[1400px] px-5 sm:px-8 h-16 flex items-center justify-between">
                <button
                    onClick={() => setMobileOpen(true)}
                    className="md:hidden text-white/80 hover:text-white"
                    data-testid="mobile-menu-open"
                    aria-label={t("nav.open_menu")}
                >
                    <Menu size={22} />
                </button>

                <nav className="hidden md:flex gap-8 flex-1">
                    <NavLink to="/" className={navLink} data-testid="nav-home" end>
                        {t("nav.home")}
                    </NavLink>
                    <NavLink to="/collection" className={navLink} data-testid="nav-collection">
                        {t("nav.collection")}
                    </NavLink>
                    <NavLink to="/collection?category=tshirt" className={navLink} data-testid="nav-tshirts">
                        {t("nav.tshirts")}
                    </NavLink>
                    <NavLink to="/collection?category=cap" className={navLink} data-testid="nav-caps">
                        {t("nav.caps")}
                    </NavLink>
                </nav>

                <Link
                    to="/"
                    className="font-display text-lg sm:text-xl font-black tracking-tighter uppercase absolute left-1/2 -translate-x-1/2"
                    data-testid="brand-logo"
                >
                    Make Rizk
                </Link>

                <div className="flex items-center gap-3 sm:gap-4 flex-1 justify-end">
                    <button
                        onClick={() => setSearchOpen(true)}
                        className="text-white/80 hover:text-white transition-colors"
                        data-testid="search-open-button"
                        aria-label={t("nav.search")}
                    >
                        <Search size={18} strokeWidth={1.5} />
                    </button>
                    <button
                        onClick={toggleLang}
                        className="hidden sm:inline-flex items-center h-8 px-2 border border-white/20 hover:border-white text-[10px] uppercase tracking-[0.2em] font-bold transition-colors"
                        data-testid="lang-toggle"
                        aria-label="Switch language"
                    >
                        {lang === "fr" ? "AR" : "FR"}
                    </button>
                    <Link
                        to="/wishlist"
                        className="relative text-white/80 hover:text-white transition-colors"
                        data-testid="wishlist-header-link"
                        aria-label={t("nav.wishlist")}
                    >
                        <Heart size={20} strokeWidth={1.5} />
                        {wishCount > 0 && (
                            <span
                                data-testid="wishlist-count-badge"
                                className="absolute -top-2 -right-2 h-5 min-w-5 px-1 bg-white text-black text-[10px] font-bold flex items-center justify-center"
                            >
                                {wishCount}
                            </span>
                        )}
                    </Link>
                    <button
                        onClick={() => setIsOpen(true)}
                        className="relative group flex items-center gap-2 text-white/80 hover:text-white transition-colors"
                        data-testid="cart-open-button"
                        aria-label={t("nav.cart")}
                    >
                        <ShoppingBag size={20} strokeWidth={1.5} />
                        {count > 0 && (
                            <span
                                data-testid="cart-count-badge"
                                className="absolute -top-2 -right-2 h-5 min-w-5 px-1 bg-white text-black text-[10px] font-bold flex items-center justify-center"
                            >
                                {count}
                            </span>
                        )}
                    </button>
                </div>
            </div>

            {/* Search overlay */}
            {searchOpen && (
                <div
                    className="fixed inset-0 z-50 bg-black/90 backdrop-blur-lg flex items-start justify-center pt-24 sm:pt-32 px-5"
                    data-testid="search-overlay"
                >
                    <button
                        onClick={() => setSearchOpen(false)}
                        className="absolute top-6 right-6 text-white/80 hover:text-white"
                        aria-label={t("nav.close")}
                        data-testid="search-close-button"
                    >
                        <X size={24} />
                    </button>
                    <form onSubmit={submitSearch} className="w-full max-w-2xl">
                        <p className="text-xs uppercase tracking-[0.3em] text-white/40 mb-4">
                            {t("nav.search")}
                        </p>
                        <input
                            autoFocus
                            type="text"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder={t("nav.search_placeholder")}
                            className="w-full h-16 bg-transparent border-b-2 border-white/20 focus:border-white text-white font-display text-2xl sm:text-4xl font-black uppercase tracking-tighter placeholder:text-white/20 outline-none pb-3"
                            data-testid="search-input"
                        />
                        <button type="submit" className="sr-only">
                            {t("nav.search")}
                        </button>
                    </form>
                </div>
            )}

            {/* Mobile drawer */}
            {mobileOpen && (
                <div className="fixed inset-0 z-50 md:hidden" data-testid="mobile-menu-drawer">
                    <div
                        className="absolute inset-0 bg-black/80"
                        onClick={() => setMobileOpen(false)}
                    />
                    <div className="absolute inset-y-0 start-0 w-[85%] max-w-sm bg-[#0a0a0a] border-e border-white/10 p-8 flex flex-col">
                        <div className="flex items-center justify-between mb-12">
                            <span className="font-display text-lg font-black uppercase tracking-tighter">
                                Make Rizk
                            </span>
                            <button
                                onClick={() => setMobileOpen(false)}
                                data-testid="mobile-menu-close"
                                aria-label={t("nav.close")}
                            >
                                <X size={22} />
                            </button>
                        </div>
                        <nav className="flex flex-col gap-6">
                            {[
                                { to: "/", label: t("nav.home"), tid: "mnav-home" },
                                { to: "/collection", label: t("nav.collection"), tid: "mnav-collection" },
                                { to: "/collection?category=tshirt", label: t("nav.tshirts"), tid: "mnav-tshirts" },
                                { to: "/collection?category=cap", label: t("nav.caps"), tid: "mnav-caps" },
                                { to: "/wishlist", label: t("nav.wishlist"), tid: "mnav-wishlist" },
                            ].map((l) => (
                                <Link
                                    key={l.tid}
                                    to={l.to}
                                    onClick={() => setMobileOpen(false)}
                                    className="font-display text-2xl font-black uppercase tracking-tighter link-underline"
                                    data-testid={l.tid}
                                >
                                    {l.label}
                                </Link>
                            ))}
                        </nav>
                        <button
                            onClick={() => {
                                toggleLang();
                                setMobileOpen(false);
                            }}
                            className="mt-8 inline-flex items-center justify-center h-10 border border-white/20 hover:border-white text-xs uppercase tracking-[0.2em] font-bold transition-colors"
                            data-testid="mobile-lang-toggle"
                        >
                            {lang === "fr" ? "العربية" : "Français"}
                        </button>
                        <div className="mt-auto pt-8 border-t border-white/10">
                            <p className="text-xs uppercase tracking-[0.2em] text-white/50">
                                {t("marquee.delivery")}
                            </p>
                            <p className="text-xs uppercase tracking-[0.2em] text-white/50 mt-1">
                                {t("marquee.cod")}
                            </p>
                        </div>
                    </div>
                </div>
            )}
        </header>
    );
}
